/* eslint-disable complexity */
import { PERMISSIONS } from '../../../packages/neb-lit-components/src/components/permissions/neb-permissions';
import { parseDate } from '../../../packages/neb-utils/date-util';
import { PAYMENT_FREQUENCY } from '../../../packages/neb-utils/enums';

// eslint-disable-next-line no-unused-vars
const [_, ANALYTICS, SCHEDULING, CHARTING, BILLING, SETTINGS] = PERMISSIONS;

export const getBillingCyclesAmount = (
  effectiveDate,
  expirationDate,
  frequency,
) => {
  if (!expirationDate) return 0;

  switch (frequency) {
    case PAYMENT_FREQUENCY.MONTHLY:
      return parseDate(expirationDate).diff(parseDate(effectiveDate), 'months');
    case PAYMENT_FREQUENCY.WEEKLY:
      return parseDate(expirationDate).diff(parseDate(effectiveDate), 'weeks');
    case PAYMENT_FREQUENCY.BI_WEEKLY:
      return Math.ceil(
        parseDate(expirationDate).diff(parseDate(effectiveDate), 'weeks') / 2,
      );
    default:
      return 0;
  }
};

export const trackMapper = {
  specialistUserAdded: (params, locations) => ({
    firstName: params.name.first,
    lastName: params.name.last,
    status: params.failureReason ? 'failed' : 'success',
    failureReason: params.failureReason,
    biography: params.biography,
    preferredLocation:
      locations.find(l => l.id === params.defaultLocationId)?.name || 'none',
    preferredLocationId: params.defaultLocationId,
    photoUploaded: Boolean(params.photo?.imageUrl),
    encounterSigningConfirmationDisabled: Boolean(params.skipSignMessage),
    createEncounterAtCheckin: Boolean(params.createEncounterAtCheckIn),
    taxIdType: params.taxIdType,
    primaryTaxonomy: params.taxonomy.label,
    defaultClaimProvider1: params.substituteProviders[0]?.providerId,
    payersProvider1: Array.isArray(params.substituteProviders[0]?.payerIds)
      ? params.substituteProviders[0].payerIds.join(',')
      : '',
    defaultClaimProvider2: params.substituteProviders[1]?.providerId,
    payersProvider2: Array.isArray(params.substituteProviders[1]?.payerIds)
      ? params.substituteProviders[1].payerIds.join(',')
      : '',
    superUserPermission: Boolean(params.superUser),
    analyticsPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === ANALYTICS.name && access,
      ),
    ),
    schedulingPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === SCHEDULING.name && access,
      ),
    ),
    chartingPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === CHARTING.name && access,
      ),
    ),
    billingPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === BILLING.name && access,
      ),
    ),
    settingsPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === SETTINGS.name && access,
      ),
    ),
    autoLogoutEnabled: Boolean(params.inactiveLogout),
    locationAccessCount: params.locations.length,
    locationIds: params.locations.join(','),
  }),
  staffUserAdded: (params, locations) => ({
    firstName: params.name.first,
    lastName: params.name.last,
    status: params.failureReason ? 'failed' : 'success',
    failureReason: params.failureReason,
    preferredLocation:
      locations.find(l => l.id === params.defaultLocationId)?.name || 'none',
    preferredLocationId: params.defaultLocationId,
    photoUploaded: Boolean(params.photo?.imageUrl),
    superUserPermission: Boolean(params.superUser),
    analyticsPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === ANALYTICS.name && access,
      ),
    ),
    schedulingPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === SCHEDULING.name && access,
      ),
    ),
    chartingPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === CHARTING.name && access,
      ),
    ),
    billingPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === BILLING.name && access,
      ),
    ),
    settingsPermission: Boolean(
      params.permissions?.find(
        ({ name, access }) => name === SETTINGS.name && access,
      ),
    ),
    autoLogoutEnabled: Boolean(params.inactiveLogout),
    locationAccessCount: params.locations?.length || 0,
    locationIds: params.locations?.join(',') || '',
  }),
  carePackageCreated: ({
    template,
    creationMethod,
    failureReason,
    ...params
  }) => {
    const isVisit = params.type === 'visit';

    let totalUnits = Number(params.totalVisits);

    if (!isVisit && params.charges?.length) {
      totalUnits = params.charges.reduce((sum, c) => sum + Number(c.units), 0);
    }

    return {
      package_template: template?.label,
      package_template_id: template?.item?.id,
      care_package_name: params.name,
      is_default: params.isDefault,
      patient_id: params.patientId,
      effective_date: params.effectiveDate?.toISOString?.(),
      expiration_date: params.expirationDate?.toISOString?.(),
      type: params.type,
      total_units: totalUnits || 0,
      total_price: params.totalPrice,
      visits_remaining: totalUnits || 0,
      creation_method: creationMethod,
      number_of_charges: params.charges?.length || 0,
      failure_reason: failureReason,
      status: failureReason ? 'failed' : 'success',
    };
  },
  carePackageSubscriptionCreated: ({
    template,
    creationMethod,
    expirationDate,
    effectiveDate,
    failureReason,
    ...params
  }) => {
    const totalBillingCycles = getBillingCyclesAmount(
      effectiveDate,
      expirationDate,
      params.frequency,
    );

    const isVisit = params.type === 'visit';

    let totalUnits = Number(params.totalVisits);

    if (!isVisit && params.charges?.length) {
      totalUnits = params.charges.reduce((sum, c) => sum + Number(c.units), 0);
    }

    return {
      package_template: template?.label,
      package_template_id: template?.item?.id,
      care_package_subscription_id: params.id,
      care_package_name: params.name,
      is_default: params.isDefault,
      patient_id: params.patientId,
      effective_date: effectiveDate?.toISOString?.(),
      expiration_date: expirationDate?.toISOString?.(),
      type: params.type,
      total_units: totalUnits || 'indefinite',
      total_price: params.totalPrice,
      sell_as_subscription: params.subscription,
      payment_processing: params.isManualSubscription ? 'manual' : 'automatic',
      number_of_charges: params.charges?.length || 0,
      billing_cycle_frequency: params.frequency,
      total_billing_cycles: totalBillingCycles || 'indefinite',
      billing_cycles_billed: 0,
      billing_cycles_remaining: totalBillingCycles || 'indefinite',
      creation_method: creationMethod,
      failure_reason: failureReason,
      status: failureReason ? 'failed' : 'success',
    };
  },
};
